import {
  publishTokenOffer,
  requestTokenFromPeers,
  subscribeAuthSync,
} from '@/utils/auth-tab-sync';
import { bumpIdleActivity } from '@/utils/idle-logout';
import tokenHelper from '@/utils/tokenHelper';
import {
  hasClientImpersonationToken,
  readSessionToken,
} from '@/utils/session-token';

let syncStarted = false;

function isUsableToken(token: string | null | undefined): token is string {
  return Boolean(token) && !tokenHelper.isTokenExpired(token ?? null);
}

async function getStore() {
  // Lazy import avoids router ↔ store ↔ hydrate cycles
  return (await import('@/store')).default;
}

/**
 * Wire cross-tab handlers once. Skips sync while a login-as `clientToken` window is active.
 */
export function startAuthTabSync() {
  if (syncStarted || typeof window === 'undefined') return;
  syncStarted = true;

  subscribeAuthSync((message) => {
    if (hasClientImpersonationToken()) return;

    if (message.type === 'TOKEN_REQUEST') {
      const token = readSessionToken();
      if (isUsableToken(token)) {
        publishTokenOffer(token);
      }
      return;
    }

    if (message.type === 'LOGOUT') {
      void getStore().then((store) =>
        store.dispatch('logout', { broadcast: false }),
      );
      return;
    }

    if (message.type === 'ACTIVITY') {
      bumpIdleActivity({ sync: false });
    }
  });
}

/**
 * If this tab has no main session token, wait briefly for a peer tab to offer one.
 * Login-as windows (`clientToken`) are left alone.
 */
export async function hydrateAuthFromPeers(): Promise<void> {
  startAuthTabSync();

  if (hasClientImpersonationToken()) return;

  const current = readSessionToken();
  if (isUsableToken(current)) return;

  const peerToken = await requestTokenFromPeers();
  if (!isUsableToken(peerToken)) return;

  const store = await getStore();
  await store.dispatch('applyPeerToken', peerToken);
}
